import React from 'react'
import PropTypes from 'prop-types'
import { flowRight, isObjectLike } from 'lodash'
import { matchPath, withRouter } from 'react-router-dom'
import { withErrorBoundaryNoRender } from 'lib/errorBoundary'
import { useTranslation } from 'react-i18next'
import { getBosPath } from 'lib/utils/store/bos'
import { graphqlPrevLocationCached } from 'lib/utils/store/navHistory'

import PrevBackNavigation from './PrevBackNavigation'
import BackNavigation from './BackNavigation'

// Routes where we do not want back nav or the back nav is better to render inside the route
// In some cases, there might be some data we want from the route component to decide
// where the back button should take us.
const ROUTES_WITHOUT_BACK = [
    '/:lang(en)?/',
    '/login',
    { path: '/:lang(en)?/user/register/:type' },
    { path: '/user/register/:type/link-account' },
    '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/pyynto/:requestId/vaihe/:task/:status',
    '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/esikatselu',
    '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/katsele',
    '/:lang(en)?/fiksut-kaupat/sahkoinen-kauppakirja/:bosId/jaa/:bosShareLinkHash',
    '/:lang(en)?/fiksut-kaupat',
    '/:lang(en)?/fiksut-kaupat/:vertical(car|bike|caravan|part|boat|machine)/:adId',
    '/:lang(en)?/tarjous/:vertical/ilmoitus/:adId',
    '/:lang(en)?/ajoneuvo/:vehicleId/tapahtumat',
    '/:lang(en)?/ajoneuvo/:vehicleId/tiedot',
    '/:lang(en)?/ajoneuvo/:vehicleId/hallinnointi',
    '/:lang(en)?/ajoneuvo/:vehicleId/jaa',
    { path: '/:lang(en)?/kayttaja/asiakirjat/*' },
    '/:lang(en)?/tarjous/:offerId(\\d+)',
    '/:lang(en)?/financing/application/:financingApplicationId',
    '/:lang(en)?/ajoneuvo/:vehicleId/luo-sahkoinen-kauppakirja',
    '/:lang(en)?/vehicle/:vehicleId/create-bill-of-sale/offer/:offerId',
    '/:lang(en)?/luo-sahkoinen-kauppakirja',
    { path: '/:lang(en)?/*-hinta-arvio' },
    '/:lang(en)?/aikajana',
    '/:lang(en)?/my-nettix-info',
    '/:lang(en)?/accu',
    '/:lang(en)?/virhe-tunnistautumisessa/:errorCode/:vertical?/:vehicleId?',
    '/:lang(en)?/jata-ilmoitus/:vehicleId/:action(aloita-kaupanteko)?',
    '/:lang(en)?/ilmoituksen-varmentaminen/:vehicleId/:action(aloita-kaupanteko)?',
    '/:lang(en)?/jata-ilmoitus/rajoitetiedot/:vehicleId/:action(aloita-kaupanteko)?',
]

const PrimaryBackNavigation = ({ match, prevLocation, ignoreBlacklist = false, preferBackRules = null }) => {
    const { t, i18n } = useTranslation(['common'])

    if (preferBackRules && prevLocation) {
        const rule = preferBackRules.find(rule => matchPath(prevLocation.pathname, rule.matchRule))

        if (rule) {
            return <BackNavigation to={rule.to} text={rule.title} />
        }
    }

    // check if blacklisted route
    if (!ignoreBlacklist && ROUTES_WITHOUT_BACK.find(rule => isObjectLike(rule) ? matchPath(match.url, rule) : rule === match.path)) {
        return null
    }

    if (match.path.match(/^\/:lang\(en\)\?\/fiksut-kaupat\/sahkoinen-kauppakirja\/:bosId\//)) {
        return (
            <BackNavigation to={getBosPath(match.params.bosId, i18n.language)} text={t('common:actions.backToBos')} />
        )
    }

    return <PrevBackNavigation />
}

PrimaryBackNavigation.propTypes = {
    ignoreBlacklist: PropTypes.bool,
    preferBackRules: PropTypes.arrayOf(
        PropTypes.shape({
            matchRule: PropTypes.object.isRequired,
            to: PropTypes.string.isRequired,
            title: PropTypes.string.isRequired,
        })
    ),
}

export default flowRight(
    withErrorBoundaryNoRender,
    graphqlPrevLocationCached,
    withRouter,
)(PrimaryBackNavigation)
